/* Basemap menu — a TOP-LEVEL control in the command bar.
 *
 * This first lived inside the Layers panel, reasoning that the basemap is just
 * another layer choice. That was wrong: every map application puts map type at the
 * top level, and an operator looking for "satellite" does not think "open layers".
 * Discoverability beats taxonomy (FR-UI-04 — learnable in a day).
 */
import { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown, Layers as LayersIcon, Map as MapIcon, Mountain, Satellite, WifiOff } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useDeck } from '../state/store'
import { BASEMAPS, basemapAvailable, styleHasDem, type BasemapId } from '../lib/basemaps'

const ICONS: Record<BasemapId, LucideIcon> = {
  tactical: MapIcon,
  satellite: Satellite,
  'terrain-light': Mountain,
  offline: WifiOff,
}

const SHORT: Record<BasemapId, string> = {
  tactical: 'Tactical',
  satellite: 'Satellite',
  'terrain-light': 'Terrain',
  offline: 'Offline',
}

export function BasemapMenu() {
  const basemap = useDeck((s) => s.basemap)
  const setBasemap = useDeck((s) => s.setBasemap)
  const terrain3d = useDeck((s) => s.terrain3d)
  const toggleTerrain3d = useDeck((s) => s.toggleTerrain3d)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const Current = ICONS[basemap]
  const demAvailable = styleHasDem(basemap)

  return (
    <div ref={ref} className="relative">
      <button
        className="ctl"
        data-active={open}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Basemap: ${SHORT[basemap]}. Click to change.`}
        title="Basemap  (B)"
      >
        <Current size={13} />
        <span style={{ fontSize: 11 }}>{SHORT[basemap]}</span>
        <ChevronDown size={11} style={{ opacity: 0.6 }} />
      </button>

      {open && (
        <div
          className="panel absolute right-0 top-8 z-40 w-56 overflow-hidden"
          role="menu"
          aria-label="Basemap"
        >
          <div className="label px-3 pb-1 pt-2">Basemap</div>

          {BASEMAPS.map((b) => {
            const Icon = ICONS[b.id]
            const active = basemap === b.id
            const unavailable = !basemapAvailable(b.id)
            return (
              <button
                key={b.id}
                role="menuitemradio"
                aria-checked={active}
                disabled={unavailable}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors hover:bg-[var(--color-surface-2)] disabled:hover:bg-transparent"
                style={{ background: active ? 'var(--color-accent-dim)' : undefined }}
                onClick={() => {
                  setBasemap(b.id)
                  setOpen(false)
                }}
                title={unavailable ? 'Not available in bundle (air-gapped) mode' : b.note}
              >
                <Icon
                  size={13}
                  style={{ color: active ? 'var(--color-accent-hi)' : 'var(--color-text-3)' }}
                  aria-hidden
                />
                <span
                  className="min-w-0 flex-1 truncate"
                  style={{ fontSize: 12, color: active ? 'var(--color-text-1)' : 'var(--color-text-2)' }}
                >
                  {b.label}
                </span>
                {active && <Check size={12} style={{ color: 'var(--color-accent-hi)' }} aria-hidden />}
              </button>
            )
          })}

          <div className="mx-3 my-1 h-px" style={{ background: 'var(--color-line-2)' }} />

          <button
            role="menuitemcheckbox"
            aria-checked={terrain3d}
            disabled={!demAvailable}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors hover:bg-[var(--color-surface-2)] disabled:hover:bg-transparent"
            onClick={toggleTerrain3d}
            title={demAvailable ? '3D elevation relief' : 'No DEM in this basemap'}
          >
            <Mountain
              size={13}
              style={{ color: terrain3d ? 'var(--color-accent-hi)' : 'var(--color-text-3)' }}
              aria-hidden
            />
            <span className="min-w-0 flex-1" style={{ fontSize: 12, color: 'var(--color-text-2)' }}>
              3D elevation
            </span>
            {terrain3d && <Check size={12} style={{ color: 'var(--color-accent-hi)' }} aria-hidden />}
          </button>

          <button
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors hover:bg-[var(--color-surface-2)]"
            onClick={() => {
              useDeck.getState().toggleLayersPanel()
              setOpen(false)
            }}
          >
            <LayersIcon size={13} style={{ color: 'var(--color-text-3)' }} aria-hidden />
            <span className="min-w-0 flex-1" style={{ fontSize: 12, color: 'var(--color-text-2)' }}>
              All layers…
            </span>
            <kbd
              className="num rounded px-1"
              style={{ fontSize: 9, background: 'var(--color-surface-3)', color: 'var(--color-text-3)' }}
            >
              L
            </kbd>
          </button>
        </div>
      )}
    </div>
  )
}
